/**
 * The "#" serial-number column the console's tables start with.
 *
 * A row's number is its 1-based position in the order the table shows it, and
 * it carries on across pages: row `index` of a page that begins after `offset`
 * rows is `offset + index + 1` (page 2 of 25-a-page starts at 26). It is
 * presentation only — CSV exports never include it.
 *
 * Two shapes, so every table kit can use it:
 *  - `RowNumberHeader` / `RowNumberCell` render the whole `<th>` / `<td>` and
 *    take the kit's own cell classes (`TH` / `TD` strings);
 *  - `RowNumberLabel` / `RowNumberValue` are just the contents, for kits whose
 *    cells are components (`<Th align="right" className={ROW_NUMBER_COL}>`).
 */

function cx(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(' ')
}

/** The serial number of row `index` (0-based) on a page that starts after `offset` rows. */
export function rowNumber(index: number, offset = 0): number {
  return offset + index + 1
}

/** Keeps the "#" column as narrow as its widest number. */
export const ROW_NUMBER_COL = 'w-px'

/** The header's contents: "#" on screen, "Row number" to a screen reader. */
export function RowNumberLabel() {
  return (
    <>
      <span aria-hidden="true">#</span>
      <span className="sr-only">Row number</span>
    </>
  )
}

/**
 * A row's number in muted tabular figures. `decorative` hides it from
 * assistive tech — for rows that already have an accessible name of their own
 * (a row that is one button), where a leading "1" would only get in the way.
 */
export function RowNumberValue({ n, decorative = false }: { n: number; decorative?: boolean }) {
  return (
    <span aria-hidden={decorative || undefined} className="tabular text-muted">
      {n}
    </span>
  )
}

/**
 * The "#" `<th>`; pass the kit's header classes. A `table-fixed` table sizes
 * columns from this row, so it passes a real `width` (e.g. `w-[60px]`).
 */
export function RowNumberHeader({ className, width = ROW_NUMBER_COL }: { className?: string; width?: string }) {
  return (
    <th scope="col" className={cx(className, width, 'text-right')}>
      <RowNumberLabel />
    </th>
  )
}

/** A row's "#" `<td>`; pass the kit's cell classes and `rowNumber(index, offset)`. */
export function RowNumberCell({ n, className, decorative = false }: { n: number; className?: string; decorative?: boolean }) {
  return (
    <td className={cx(className, ROW_NUMBER_COL, 'text-right')}>
      <RowNumberValue n={n} decorative={decorative} />
    </td>
  )
}
